import { AppError } from "../errors/types";
import { reportAppError } from "../observability/crashReporter";
import { estimateTtsCostUsd, logMetric } from "../observability/metrics";
import { stripSsmlTags } from "../lib/ssml";
import { useChatStore } from "../state/chatStore";
import { lookupPrerendered } from "../audio/ttsAssets";
import { toSpeechText } from "../audio/speechText";

export async function synthesizeSpeech(
  text: string,
  voiceId?: string
): Promise<{ audio: Blob; durationMs: number; model: string }> {
  const startedAt = performance.now();

  // 画面表示テキスト (text) ではなく読み上げテキストを合成対象にする (spec §9.1)。
  // 変換はここ 1 箇所だけで行い、以降 (事前生成の照会・API 送信・chars 計測) は
  // すべて speechText を使う。呼び出し側は画面表示用の text をそのまま渡せばよく、
  // 変更不要 (signature 不変)。
  const speechText = toSpeechText(text);

  // 固定文言は事前生成済みの mp3 を使う (spec 2026-07-26)。合成待ちが消え、
  // API 課金も発生しない。voiceId が明示指定された場合は manifest と別の声に
  // なるので使わない。取得できなければ null が返り、下の API 経路に落ちる (原則 5)。
  if (!voiceId) {
    const prerendered = await lookupPrerendered(speechText);
    if (prerendered) {
      const roundTripMs = Math.round(performance.now() - startedAt);
      // 事前生成でも観測は続ける (原則 6)。cost 0 で記録することで、
      // summary の TTS コスト集計に「削減できた分」が反映される。
      logMetric({
        kind: "tts",
        provider: "elevenlabs",
        recordedAt: Date.now(),
        sessionId: useChatStore.getState().sessionId ?? undefined,
        chars: stripSsmlTags(speechText).length,
        serverDurationMs: null,
        roundTripMs,
        model: "prerendered",
        estimatedCostUsd: 0,
      });
      return { audio: prerendered, durationMs: roundTripMs, model: "prerendered" };
    }
  }

  const fetchOnce = () =>
    fetch("/api/tts/elevenlabs", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ text: speechText, voiceId }),
    });

  let res: Response;
  try {
    res = await fetchOnce();
    if (!res.ok && res.status >= 500) {
      res = await fetchOnce();
    }
  } catch (e) {
    // 回線断はここで raw TypeError になり AppError にすらならない
    // (spec 2026-07-29 §1.3(a))。structureClient と同じ形で塞ぐ。
    reportAppError({
      code: "tts-failed",
      reason: "network",
      context: "tts",
      message: e instanceof Error ? e.message : String(e),
    });
    throw e;
  }
  if (!res.ok) {
    const body = await safeJson(res);
    reportAppError({
      code: "tts-failed",
      reason: "http",
      context: "tts",
      message: body.error ?? `tts failed: ${res.status}`,
      httpStatus: res.status,
    });
    throw new AppError("tts-failed", body.error ?? `読み上げに失敗しました (${res.status})`);
  }

  const buf = await res.arrayBuffer();
  const audio = new Blob([buf], { type: "audio/mpeg" });
  const serverDurationMs = numberOr(res.headers.get("x-tts-duration-ms"), null);
  const model = res.headers.get("x-tts-model") ?? "eleven_flash_v2_5";
  const roundTripMs = Math.round(performance.now() - startedAt);

  // SSML `<break>` タグは実際には発話されないので、metric.chars は
  // タグ除去後の実発話文字数で計算する (コスト概算・比較のため)
  const spokenChars = stripSsmlTags(speechText).length;
  // I3: aggregateSummary の cross-session 集計汚染防止のため sessionId を同梱する。
  const sessionId = useChatStore.getState().sessionId ?? undefined;
  logMetric({
    kind: "tts",
    provider: "elevenlabs",
    recordedAt: Date.now(),
    sessionId,
    chars: spokenChars,
    serverDurationMs,
    roundTripMs,
    model,
    estimatedCostUsd: estimateTtsCostUsd(spokenChars),
  });

  return { audio, durationMs: serverDurationMs ?? roundTripMs, model };
}

function numberOr(raw: string | null, fallback: number | null): number | null {
  if (raw == null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

async function safeJson(res: Response): Promise<{ error?: string }> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}
